import { SearchIcon } from "@heroicons/react/outline";
import { useMemo, useState } from "react";

import DatasetGroupModal from "@/components/DatasetGroupModal";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUser } from "@/context/user-context";
import { Link, useNavigate } from "react-router-dom";
import { Button, DivButton } from "../ui/button";
import { Input } from "../ui/input";
import { useGetDatasetGroups } from "@/queries/datasets/useGetDatasetGroups";
import { DatasetCard } from "../Datasets/DatasetCard";
import { GenericTablePagination } from "../ui/table-pagination";
import { DatasetGroup } from "@/queries/datasets/types";

interface DatasetGroupSelectorModalProps {
  handleDatasetGroupIdClick: (
    datasetGroupId: DatasetGroup,
  ) => void | Promise<void>;
  isLoading?: boolean;
  minVersionNumber?: number;
  setOpen: (isOpen: boolean) => void;
  showCreateButton?: boolean;
}

const DatasetGroupSelectorModal = ({
  handleDatasetGroupIdClick,
  setOpen,
  showCreateButton = false,
  minVersionNumber,
}: DatasetGroupSelectorModalProps) => {
  const [searchValue, setSearchValue] = useState("");
  const userContext = useUser();
  const navigate = useNavigate();
  const [isDatasetGroupModalOpen, setDatasetGroupModalOpen] = useState(false);
  const {
    data: datasetGroups,
    isLoading: isDatasetGroupsLoading,
    page,
    setPage,
    pageSize,
    setPageSize,
  } = useGetDatasetGroups({
    search: searchValue,
    minVersionNumber,
  });

  const datasetGroupList = useMemo(() => {
    return (
      datasetGroups?.dataset_groups?.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ) ?? []
    );
  }, [datasetGroups]);

  const handleDatasetGroupClick = async (datasetGroup: DatasetGroup) => {
    await handleDatasetGroupIdClick(datasetGroup);
    setOpen(false);
  };

  return (
    <Dialog onOpenChange={setOpen} open={true}>
      <DialogContent className="flex h-full max-h-[986px] w-full max-w-[672px] flex-col overflow-y-auto">
        <DialogHeader>
          <div className="mb-2 flex flex-col gap-y-1">
            <DialogTitle>Select a Dataset</DialogTitle>
            <DialogDescription>
              <span className="block max-w-md">
                Select a dataset for batch evaluations. You can upload a new
                dataset on the{" "}
                <Link
                  to={`/workspace/${userContext.activeWorkspaceId}/datasets`}
                  className="text-blue-500 hover:text-blue-600"
                >
                  datasets page
                </Link>
                .
              </span>
            </DialogDescription>
          </div>
        </DialogHeader>
        <div className="flex h-full flex-col gap-3 overflow-y-auto">
          <div className="relative mb-2 flex items-center gap-x-2">
            <SearchIcon className="absolute left-2 h-5 w-5" />
            <Input
              type="text"
              placeholder="Search by column name or dataset name"
              className="w-full pl-10"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
            />
          </div>
          {isDatasetGroupsLoading ? (
            <div className="mt-4 text-center">
              <LoadingSpinner size={5} />
            </div>
          ) : (
            <div className="flex h-full flex-col justify-between gap-3 overflow-y-auto pb-3">
              <div className="flex flex-col gap-3">
                {datasetGroupList.map((datasetGroup) => (
                  <DatasetCard
                    key={datasetGroup.id}
                    onClick={() => handleDatasetGroupClick(datasetGroup)}
                    datasetGroup={datasetGroup}
                  />
                ))}
              </div>
              {datasetGroupList.length === 0 && (
                <div className="col-span-2 flex flex-col items-center justify-center gap-y-2 text-center text-gray-400">
                  No datasets found.
                  <p className="flex items-center text-xs text-blue-500">
                    If you have a new dataset make sure you have saved your
                    draft.
                  </p>
                  <DivButton
                    variant="outline"
                    onClick={() => {
                      navigate(
                        `/workspace/${userContext.activeWorkspaceId}/datasets`,
                      );
                    }}
                    size="sm"
                  >
                    Go to Datasets
                  </DivButton>
                </div>
              )}
            </div>
          )}
        </div>
        {isDatasetGroupModalOpen && (
          <DatasetGroupModal
            handleDatasetCreated={(res) =>
              handleDatasetGroupClick(res.dataset_group.id)
            }
            setOpen={setDatasetGroupModalOpen}
          />
        )}
        <DialogFooter className="flex w-full items-center !justify-start gap-x-2">
          <GenericTablePagination
            page={page}
            pageSize={pageSize}
            pageSizeOptions={[10, 20, 50, 100]}
            setPageIndex={setPage}
            setPageSize={setPageSize}
            pages={datasetGroups?.pages || 0}
            hasNext={datasetGroups?.has_next || false}
            hasPrev={datasetGroups?.has_prev || false}
          />
        </DialogFooter>
        {showCreateButton && (
          <Button onClick={() => setDatasetGroupModalOpen(true)}>
            Create New Dataset
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DatasetGroupSelectorModal;
